/* eslint-disable @typescript-eslint/no-non-null-assertion */
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useContext, useState } from 'react';
import { usePermissions, UserContext } from '../../../../../providers/userContextProvider';
import { Typography, Box, Skeleton } from '../../../../1-primative';
import { usePageState } from '../../../../../util/usePageState';
import {
  Table, TableHeadCell, TableRow, TableCell, TableBody, Menu,
  MenuItem, IconButton, Badge, Card, CardContent, Pagination,
} from '../../../../2-component';
import ConfirmationDialog from '../../../../3-pattern/confirmationDialog/confirmationDialog';
import { SettingsTitle } from '../../../../3-pattern';
import { UpdateOrganizationSwitch } from '../../../../4-module/updateOrganizationSwitch/updateOrganizationSwitch';
import { translateBackend } from '../../../../../assets/i18n/config';
import { NewFormDefinition } from './newFormDefinition';
import { EditFormDefinition } from './editFormDefinition';

const FETCH_FORM_DEFINITIONS = gql`
  query fetchFormDefinitions($input: FetchFormDefinitionsInput!) {
    fetchFormDefinitions(input: $input) {
      formDefinitions {
        id
        key
        state
        signingType
        templateType
        s3Url
        mappingWebhook {
          url
          apiToken
          type
        }
        signerMapping {
          signerKey
          pageNumber
          x
          y
        }
        translatedName {
          en
          fr
        }
        translatedDescription {
          en
          fr
        }
        objectType
        version
      }
      totalCount
    }
  }
`;

const TRANSITION_FORM_DEFINITION = gql`
  mutation transitionFormDefinition($input: TransitionFormDefinitionInput!) {
    transitionFormDefinition(input: $input) {
      formDefinition {
        id
      }
    }
  }
`;

export const Definitions = () => {
  const { t } = useTranslation(['settings']);
  const { permissions } = usePermissions();
  const { activeOrganization } = useContext(UserContext);
  const [selectedDefinition, setSelectedDefinition] = useState<any>();
  const [editFormDefinition, setEditFormDefinition] = useState<any>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [page, setPage] = usePageState(1, 'page');
  const pageSize = 50;
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    loading, error, data, previousData, refetch,
  } = useQuery(FETCH_FORM_DEFINITIONS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
        },
        pagination: {
          sortField: 'createdAt', sortDesc: false, perPage: pageSize, offSet: (page - 1) * pageSize,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const [transitionFormDefinition] = useMutation(TRANSITION_FORM_DEFINITION, {
    refetchQueries: [FETCH_FORM_DEFINITIONS],
  });

  const handleTransition = async ({ formDefinitionId, transition }: { formDefinitionId: string; transition: string }) => {
    await transitionFormDefinition({
      variables: {
        input: {
          formDefinitionId,
          transition,
        },
      },
    });
  };

  if (error) return <Typography>Error</Typography>;

  return (
    <>
      <SettingsTitle title={t('settings:formDefinitions.title')} description={t('settings:formDefinitions.description')} />
      <Card loading={loading}>
        <CardContent>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Box>
              {permissions.includes('write:advanced_organization_settings') && (
                <UpdateOrganizationSwitch
                  field='useParentFormDefinitions'
                  label={t('settings:formDefinitions.useParentFormDefinitions')}
                  organizationId={activeOrganization.id}
                  refetchQueries={[FETCH_FORM_DEFINITIONS]}
                />
              )}
              </Box>
            <NewFormDefinition defaultOrg={activeOrganization.id!} afterCreate={() => refetch()} />
          </Box>
        </CardContent>
        <Table>
          <TableBody>
            <TableRow>
              <TableHeadCell>{t('settings:formDefinitions.table.key')}</TableHeadCell>
              <TableHeadCell>{t('settings:formDefinitions.table.name')}</TableHeadCell>
              <TableHeadCell>{t('settings:formDefinitions.table.type')}</TableHeadCell>
              <TableHeadCell>{t('settings:formDefinitions.table.objectType')}</TableHeadCell>
              <TableHeadCell>{t('settings:formDefinitions.table.state')}</TableHeadCell>
              <TableHeadCell right>{t('settings:formDefinitions.table.actions')}</TableHeadCell>
            </TableRow>
            {loading && !previousData && [...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton width='100%' /></TableCell>
                <TableCell><Skeleton width='100%' /></TableCell>
                <TableCell><Skeleton width='100%' /></TableCell>
                <TableCell><Skeleton width='100%' /></TableCell>
                <TableCell><Skeleton width='100%' /></TableCell>
                <TableCell><Skeleton width='100%' /></TableCell>
              </TableRow>
            ))}
            {(data || previousData)?.fetchFormDefinitions?.formDefinitions?.map((definition: any) => (
              <TableRow hover key={definition.id} onClick={() => setEditFormDefinition(definition)} pointer>
                <TableCell>
                  <Typography>{definition.key}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{translateBackend(definition.translatedName)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{t(`formDefinitions.formDefinitionModal.${definition.templateType}_${definition.signingType}`)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{t(`settings:formDefinitions.objectType.${definition.objectType}`)}</Typography>
                </TableCell>
                <TableCell>
                  <Badge
                    label={t(`settings:formDefinitions.state.${definition.state}`)}
                    color={definition.state === 'ACTIVE' ? 'positive' : 'negative'}
                  />
                </TableCell>
                <TableCell right>
                  <IconButton
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      e.preventDefault();
                      setAnchorEl(e.currentTarget);
                      setSelectedDefinition(definition);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination
          count={Math.ceil(((data || previousData)?.fetchFormDefinitions?.totalCount ?? 0) / pageSize)}
          page={page}
          onChange={(_e, newPage) => setPage(newPage)}
          sx={{
            p: 1,
            textAlign: 'right',
            '.MuiPagination-ul': {
              justifyContent: 'end',
            },
          }}
        />

        {selectedDefinition && (
          <>
            <Menu
              anchorEl={anchorEl}
              id='form-definition-menu'
              open={open}
              onClose={handleClose}
              onClick={handleClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {selectedDefinition.state === 'DRAFT' && (
                <MenuItem onClick={() => handleTransition({ formDefinitionId: selectedDefinition.id, transition: 'activate' })}>
                  {t('shared:activate')}
                </MenuItem>
              )}
              <MenuItem onClick={() => setOpenDeleteDialog(true)}>{t('shared:archive')}</MenuItem>
            </Menu>

            <ConfirmationDialog
              onConfirm={async () => {
                await handleTransition({ formDefinitionId: selectedDefinition.id, transition: 'archive' });
                setOpenDeleteDialog(false);
              }}
              onCancel={() => setOpenDeleteDialog(false)}
              title={t('formDefinitions.archiveModal.title')}
              open={openDeleteDialog}
            >
              <Typography variant='bodyLarge'>{t('formDefinitions.archiveModal.body')}</Typography>
            </ConfirmationDialog>
          </>
        )}
      </Card>
      {editFormDefinition && (
        <EditFormDefinition
          formDefinitionToUpdate={editFormDefinition}
          handleClose={() => setEditFormDefinition(null)}
          afterUpdate={() => {
            refetch();
            setEditFormDefinition(null);
          }}
          defaultOrg={activeOrganization.id!}
        />
      )}
    </>
  );
};
